import React from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragOverlay 
} from "@dnd-kit/core";
import { Tag, Popconfirm, Checkbox } from "antd";
import { EyeOutlined, FileExcelOutlined, DeleteOutlined } from "@ant-design/icons";
import { createPortal } from "react-dom";

// Constante de estados permitidos
const ESTADOS = ["todo", "doing", "done"];

const DraggableItem = ({ item, onClick, onEliminar, selected, onToggleSelect, renderExtra, textoQuitar }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: item._id });

  const style = {
    // Cuando hay overlay, oculta el original para que no se vea "doble"
    opacity: isDragging ? 0 : 1,        // <— antes 0.5
    visibility: isDragging ? "hidden" : "visible", // <— opcional pero ayuda
    // Evita que el original “capture” eventos o parpadee
    pointerEvents: isDragging ? "none" : "auto",

    // El transform del ítem original ya no importa si está oculto,
    // pero lo dejamos por si no hay overlay.
    transform: transform
      ? `translate(${transform.x}px, ${transform.y}px)`
      : undefined,

    position: "relative",
    zIndex: 1,
    padding: 8,
    margin: "8px 0",
    background: "#f9f9f9",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    border: selected ? "2px solid #00BFD6" : "2px solid transparent",
    cursor: "grab",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      role="button"
      onClick={() => onClick(item._id)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
          <span
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox checked={selected} onChange={() => onToggleSelect(item._id)} />
          </span>
          <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 4, color: "#333" }}>
            {item.nombre}
          </div>
        </div>
        {onEliminar && (
          <span
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Popconfirm
              title={textoQuitar || "¿Quitar esta agrupación del tablero?"}
              onConfirm={() => onEliminar(item._id)}
              okText="Sí"
              cancelText="No"
            >
              <DeleteOutlined style={{ color: "#c0392b", cursor: "pointer" }} />
            </Popconfirm>
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#666" }}>{item.descripcion}</div>
      {(item.ventaId || item.cotizacionId) && item.textoTag && (
        <Tag color={item.colorTag} style={{ marginTop: 8 }}>
          {item.textoTag}
        </Tag>
      )}
      {renderExtra && renderExtra(item)}
    </div>
  );
};

const DroppableColumn = ({ id, title, actividades, onClick, onEliminar, onVerClick, onExportarExcel, disableExcel, disableVer, selectedIds, onToggleSelect, renderExtra, textoQuitar, ancho }) => {
  const { setNodeRef } = useDroppable({ id });
  const contarActividades = actividades?.length || 0;

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: `0 0 ${ancho || 320}px`,   // ancho fijo de columna
        minWidth: Math.min(ancho || 320, 280),       // evita que se comprima
        background: "#f4f4f4",
        borderRadius: 8,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        maxHeight: "70vh",
      }}
    >
      {/* Título fijo */}
      <h3
        style={{
          textAlign: "center",
          fontSize: 15,
          position: "sticky",
          top: 0,
          background: "#f4f4f4",
          zIndex: 1,
          paddingBottom: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {title} {contarActividades ? `(${contarActividades})` : null}

        {!disableVer && (
          <EyeOutlined
            style={{
              fontSize: 14,
              cursor: "pointer",
              color: "#555",
            }}
            onClick={() => onVerClick(id, actividades)} // reemplaza por tu función
          />
        )}
        {!disableExcel && (
          <FileExcelOutlined
            style={{
              fontSize: 14,
              cursor: "pointer",
              color: "#1D6F42",
            }}
            onClick={() => onExportarExcel(id, actividades)} // reemplaza por tu función
          />
        )}
      </h3>

      {/* Área scrollable */}
      <div
        className="scrollbar-pequeña"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          flexGrow: 1,
        }}
      >
        {actividades.length === 0 ? (
          <div style={{ textAlign: "center", color: "#999", marginTop: 20, fontSize: 14 }}>
            Sin tareas
          </div>
        ) : (
          actividades.map((act) => (
            <DraggableItem
              key={act._id}
              item={act}
              onClick={onClick}
              onEliminar={onEliminar}
              selected={selectedIds.has(act._id)}
              onToggleSelect={onToggleSelect}
              renderExtra={renderExtra}
              textoQuitar={textoQuitar}
            />
          ))
        )}
      </div>
    </div>
  );
};

function ordenarEtapasPorRelacion(etapas) {
  const porId = Object.fromEntries(etapas.map(e => [e._id, e]));
  const yaVisitados = new Set();
  const ordenadas = [];

  const primeras = etapas.filter(e => e.etapaAnterior?.length === 0);

  function recorrer(etapa) {
    if (!etapa || yaVisitados.has(etapa._id)) return;

    yaVisitados.add(etapa._id);
    ordenadas.push(etapa);

    const siguientes = etapa.etapaSiguiente || [];
    for (const sig of siguientes) {
      recorrer(porId[sig._id]);
    }
  }

  for (const etapaInicial of primeras) {
    recorrer(etapaInicial);
  }

  for (const etapa of etapas) {
    if (!yaVisitados.has(etapa._id)) {
      ordenadas.push(etapa);
    }
  }

  return ordenadas;
}

// Props opcionales (las usa el tablero "Por persona" del Dashboard de Taxidermia):
// - renderExtra(item): contenido adicional al pie de cada tarjeta.
// - textoQuitar: texto del Popconfirm del bote de basura.
// - disableVer: oculta el ojo del encabezado de columna.
// - anchoColumna: ancho fijo de cada columna (320 por omision).
const KanbanTablero = ({ etapas, onActividadMove, onActividadMoveMultiple, onActividadClick, onActividadEliminar, onEtapaVer, onEtapaExcel, disableExcel, disableVer, renderExtra, textoQuitar, anchoColumna }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const [activeItem, setActiveItem] = React.useState(null);
  const [dragEsGrupal, setDragEsGrupal] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());

  const etapasOrdenadas = ordenarEtapasPorRelacion(etapas);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) {
        nuevo.delete(id);
      } else {
        nuevo.add(id);
      }
      return nuevo;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDragStart = (event) => {
    const item = etapas
      .flatMap(e => e.actividades || [])
      .find(a => a._id === event.active.id);
    setActiveItem(item || null);
    setDragEsGrupal(selectedIds.has(event.active.id) && selectedIds.size > 1);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    const esGrupal = dragEsGrupal;
    setActiveItem(null);
    setDragEsGrupal(false);
    if (!over || active.id === over.id) return;

    if (esGrupal) {
      onActividadMoveMultiple(Array.from(selectedIds), over.id);
      clearSelection();
    } else {
      onActividadMove(active.id, over.id);
    }
  };

  const handleActividadClick = (id) => onActividadClick(id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {selectedIds.size > 0 && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            background: "#e6f7fa",
            border: "1px solid #00BFD6",
            borderRadius: 20,
            padding: "4px 12px",
            marginBottom: 8,
            fontSize: 13,
            color: "#00707d",
          }}
        >
          {selectedIds.size} seleccionada{selectedIds.size > 1 ? "s" : ""} — arrastra alguna a otra columna para moverlas juntas
          <span
            role="button"
            onClick={clearSelection}
            style={{ cursor: "pointer", fontWeight: "bold" }}
          >
            ✕
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, padding: "20px 0px", overflowX: "auto" }}>
        {etapasOrdenadas.map((etapa, index) => (
          <DroppableColumn
            key={etapa._id}
            id={etapa._id}
            title={etapa.nombre}
            actividades={etapa.actividades || []}
            onClick={handleActividadClick}
            onEliminar={onActividadEliminar}
            onVerClick={(id, tareas) => onEtapaVer && onEtapaVer(id, tareas, etapa, etapasOrdenadas[index + 1])}
            onExportarExcel={(id, tareas) => onEtapaExcel && onEtapaExcel(id, tareas, etapa, etapasOrdenadas[index + 1])}
            disableExcel={disableExcel}
            disableVer={disableVer}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            renderExtra={renderExtra}
            textoQuitar={textoQuitar}
            ancho={anchoColumna}
          />
        ))}
      </div>

      {/* Overlay: render fuera del flujo, no se oculta ni se recorta */}
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            // Clon visual SIN useDraggable (solo presentación)
            <div
              style={{
                position: "relative",
                padding: 8,
                margin: "8px 0",
                background: "#f9f9f9",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                cursor: "grabbing",
              }}
            >
              {dragEsGrupal && (
                <span
                  style={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    background: "#00BFD6",
                    color: "#fff",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: "bold",
                    padding: "2px 8px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  }}
                >
                  +{selectedIds.size - 1} más
                </span>
              )}
              <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 4, color: "#333" }}>
                {activeItem.nombre}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>{activeItem.descripcion}</div>
              {(activeItem.ventaId || activeItem.cotizacionId) && (
                <Tag color={activeItem.colorTag} style={{ marginTop: 8 }}>
                  {activeItem.textoTag}
                </Tag>
              )}
            </div>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

// Validación de props
KanbanTablero.propTypes = {
  etapas: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      estadoMock: PropTypes.oneOf(ESTADOS).isRequired,
    })
  ).isRequired,
  onEstadoChange: PropTypes.func.isRequired,
  onActividadClick: PropTypes.func.isRequired, // Función para manejar el clic
  onActividadMoveMultiple: PropTypes.func.isRequired, // Mueve un grupo de actividades seleccionadas a una etapa
};

export default KanbanTablero;